import { Box, Button, Card, Typography, useColorScheme } from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import { BookMode } from "../App";
import SapphireShoreArtPiece from "../assets/SapphireShoreArtPiece.webp";
import TheSecondOneArtPiece from "../assets/TheSecondOneArtPiece.webp";
import TheThirdOneArtPiece from "../assets/TheThirdOneArtPiece.webp";

interface BookSelectorProps {
  selectedBook: BookMode;
  onBookSelect: (book: BookMode) => void;
}

const books = [
  {
    id: "sapphire-shore" as BookMode,
    title: "The Sapphire Shore",
    subtitle: "Book 1",
    status: "Available Now",
    image: SapphireShoreArtPiece,
    color: "rgba(59, 130, 246, 0.8)", // Blue
    description: "The first volume in the epic fantasy series",
  },
  {
    id: "ember-tide" as BookMode,
    title: "The Ember Tide",
    subtitle: "Book 2",
    status: "Available Now",
    image: TheSecondOneArtPiece,
    color: "rgba(239, 68, 68, 0.8)", // Red
    description: "The world shattering second volume continuing Osgara's tale",
  },
  {
    id: "third-book" as BookMode,
    title: "???",
    subtitle: "Book 3",
    status: "Coming 2027",
    image: TheThirdOneArtPiece,
    color: "rgba(168, 85, 247, 0.8)", // Purple
    description: "",
  },
];

export default function BookSelector({
  selectedBook,
  onBookSelect,
}: BookSelectorProps) {
  const theme = useTheme();
  const { mode } = useColorScheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = mode === "dark";

  return (
    <Box
      sx={{
        py: 4,
        mb: 6,
      }}
    >
      {/* <Typography
        level="h2"
        sx={{
          textAlign: "center",
          mb: 4,
          background: isDark
            ? "linear-gradient(45deg, #ffffff 0%, #e0e7ff 100%)"
            : "linear-gradient(45deg, #1e293b 0%, #334155 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "0.05em",
        }}
      >
        Choose Your Journey
      </Typography> */}

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 3,
          justifyContent: "center",
          alignItems: "stretch",
        }}
      >
        {books.map((book) => (
          <Card
            key={book.id}
            variant="outlined"
            sx={{
              flex: 1,
              maxWidth: isMobile ? "100%" : "350px",
              cursor: "pointer",
              position: "relative",
              overflow: "hidden",
              background:
                selectedBook === book.id
                  ? isDark
                    ? `linear-gradient(135deg, ${book.color} 0%, rgba(0, 0, 0, 0.8) 100%)`
                    : `linear-gradient(135deg, ${book.color}40 0%, rgba(255, 255, 255, 0.9) 100%)`
                  : isDark
                  ? "rgba(0, 0, 0, 0.4)"
                  : "rgba(255, 255, 255, 0.6)",
              border:
                selectedBook === book.id
                  ? `2px solid ${book.color}`
                  : isDark
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              backdropFilter: "blur(10px)",
              transition: "all 0.3s ease",
              transform: selectedBook === book.id ? "scale(1.02)" : "scale(1)",
              "&:hover": {
                transform: "scale(1.05)",
                border: `2px solid ${book.color}`,
                boxShadow: `0 10px 30px ${book.color}40`,
              },
            }}
            onClick={() => onBookSelect(book.id)}
          >
            {/* Background image with overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${book.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity:
                  selectedBook === book.id
                    ? isDark
                      ? 0.3
                      : 0.2
                    : isDark
                    ? 0.1
                    : 0.05,
                transition: "opacity 0.3s ease",
                zIndex: -1,
              }}
            />

            <Box
              sx={{
                p: 3,
                textAlign: "center",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Typography
                level="body-sm"
                sx={{
                  color: book.color,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  mb: 1,
                  fontWeight: 600,
                }}
              >
                {book.subtitle}
              </Typography>

              <Typography
                level="title-lg"
                sx={{
                  color: isDark ? "white" : "#1e293b",
                  mb: 2,
                  fontWeight: 300,
                  letterSpacing: "0.02em",
                }}
              >
                {book.title}
              </Typography>

              <Typography
                level="body-sm"
                sx={{
                  color: isDark
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(30, 41, 59, 0.7)",
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                {book.description}
              </Typography>

              <Button
                variant={selectedBook === book.id ? "solid" : "outlined"}
                size="sm"
                sx={{
                  background:
                    selectedBook === book.id
                      ? `linear-gradient(45deg, ${book.color}, ${book.color}cc) !important`
                      : "transparent !important",
                  backgroundColor:
                    selectedBook === book.id
                      ? `${book.color} !important`
                      : "transparent !important",
                  border: `1px solid ${book.color}`,
                  color: selectedBook === book.id ? "white" : book.color,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontSize: "0.75rem",
                  fontFamily: "'Cinzel', serif",
                  "&:hover": {
                    background: `linear-gradient(45deg, ${book.color}, ${book.color}cc) !important`,
                    backgroundColor: `${book.color} !important`,
                    color: "white",
                  },
                  "&:active": {
                    background: `linear-gradient(45deg, ${book.color}dd, ${book.color}) !important`,
                    backgroundColor: `${book.color} !important`,
                    color: "white",
                  },
                  "&:focus-visible": {
                    outlineColor: book.color,
                  },
                }}
              >
                {book.status}
              </Button>

              {selectedBook === book.id && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: book.color,
                    boxShadow: `0 0 10px ${book.color}`,
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 1 },
                      "50%": { opacity: 0.5 },
                      "100%": { opacity: 1 },
                    },
                  }}
                />
              )}
            </Box>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
